import type { OrderbookEvent, PriceLevel } from "@ekidenfi/ts-sdk";
import { makeAutoObservable } from "mobx";

const MAX_LEVELS = 100;
const TRIM_THRESHOLD = 150; // Only trim when exceeds this
const TRIM_THROTTLE_MS = 1000; // Trim at most once per second

export class OrderbookStore {
  asks = new Map<string, number>();
  bids = new Map<string, number>();
  initialized = false;

  private trimPending = false;
  private trimTimerId: ReturnType<typeof setTimeout> | null = null;
  private lastTrimTime = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setInitialized = (value: boolean) => {
    this.initialized = value;
  };

  setInitializedEmpty = () => {
    this.asks.clear();
    this.bids.clear();
    this.initialized = true;
  };

  processMessage = (event: OrderbookEvent) => {
    if (!this.initialized) {
      this.asks.clear();
      this.bids.clear();
    }

    this.applyDeltas(event.a, this.asks);
    this.applyDeltas(event.b, this.bids);
    this.scheduleTrim();

    if (!this.initialized) {
      this.setInitialized(true);
    }
  };

  reset = () => {
    this.initialized = false;
    this.asks.clear();
    this.bids.clear();
    if (this.trimTimerId !== null) {
      clearTimeout(this.trimTimerId);
      this.trimTimerId = null;
    }
    this.trimPending = false;
  };

  private applyDeltas = (levels: PriceLevel[], bookSide: Map<string, number>) => {
    for (const [price, size] of levels) {
      if (size === "0") {
        bookSide.delete(price);
      } else {
        bookSide.set(price, Number(size));
      }
    }
  };

  private scheduleTrim = () => {
    // Only schedule if we actually need trimming
    if (this.asks.size <= TRIM_THRESHOLD && this.bids.size <= TRIM_THRESHOLD) {
      return;
    }

    // Already scheduled
    if (this.trimPending) {
      return;
    }

    const now = Date.now();
    const timeSinceLastTrim = now - this.lastTrimTime;

    if (timeSinceLastTrim >= TRIM_THROTTLE_MS) {
      // Can trim immediately
      this.trimToMaxLevels();
    } else {
      // Schedule for later
      this.trimPending = true;
      this.trimTimerId = setTimeout(() => {
        this.trimTimerId = null;
        this.trimPending = false;
        this.trimToMaxLevels();
      }, TRIM_THROTTLE_MS - timeSinceLastTrim);
    }
  };

  private trimToMaxLevels = () => {
    this.lastTrimTime = Date.now();

    if (this.asks.size > MAX_LEVELS) {
      const sortedAsks = Array.from(this.asks.entries()).sort((a, b) => Number(a[0]) - Number(b[0]));
      const toKeep = sortedAsks.slice(0, MAX_LEVELS);
      this.asks.clear();
      for (const [price, size] of toKeep) {
        this.asks.set(price, size);
      }
    }

    if (this.bids.size > MAX_LEVELS) {
      const sortedBids = Array.from(this.bids.entries()).sort((a, b) => Number(b[0]) - Number(a[0]));
      const toKeep = sortedBids.slice(0, MAX_LEVELS);
      this.bids.clear();
      for (const [price, size] of toKeep) {
        this.bids.set(price, size);
      }
    }
  };
}
